export function generateGEDCOM(subjectName, subjectRecord, members, structure = {}) {
  const lines = [];
  const now   = new Date();
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const gedDate = `${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;

  // Helper: sanitise a string for GEDCOM (no newlines, trim)
  const esc = s => (s || '').replace(/[\r\n]+/g, ' ').trim();

  // Helper: parse "YYYY", "YYYY-MM", "YYYY-MM-DD" → GEDCOM date string
  const gedYear = yr => {
    if (!yr) return '';
    const parts = String(yr).trim().split('-');
    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${parseInt(d,10)} ${months[parseInt(m,10)-1]} ${y}`;
    }
    if (parts.length === 2) {
      return `${months[parseInt(parts[1],10)-1]} ${parts[0]}`;
    }
    return parts[0]; // just year
  };

  // Helper: split a full name into given/surname for GEDCOM /SURNAME/ convention
  const splitName = fullName => {
    const parts = esc(fullName).trim().split(' ');
    if (parts.length === 1) return { given: parts[0], surname: '' };
    const surname = parts[parts.length - 1];
    const given   = parts.slice(0, -1).join(' ');
    return { given, surname };
  };

  // Assign GEDCOM IDs
  const SUBJ_ID  = '@I1@';
  const memberIds = {};
  members.forEach((m, i) => { memberIds[m.id] = `@I${i + 2}@`; });
  const relationships = Array.isArray(structure.relationships) ? structure.relationships : [];
  const groupList = Array.isArray(structure.groups) ? structure.groups : [];
  const famsLinks = {};
  const famcLinks = {};

  // ── HEADER ────────────────────────────────────────────────────
  lines.push('0 HEAD');
  lines.push('1 SOUR GivingVoiceToSilence');
  lines.push('2 NAME Giving Words to Voices\u2122');
  lines.push('2 VERS 1.0');
  lines.push('1 DATE ' + gedDate);
  lines.push('1 GEDC');
  lines.push('2 VERS 5.5.1');
  lines.push('2 FORM LINEAGE-LINKED');
  lines.push('1 CHAR UTF-8');
  lines.push('1 LANG English');

  let familyUnits = relationships.map((rel, idx) => ({
    id: rel.id || `rel_${idx + 1}`,
    famId: `@F${idx + 1}@`,
    partnerA: rel.partnerA,
    partnerB: rel.partnerB,
    children: Array.isArray(rel.children) ? rel.children : [],
    label: rel.label || '',
  }));

  if (familyUnits.length === 0) {
    const father  = members.find(m => m.relation === 'father');
    const mother  = members.find(m => m.relation === 'mother');
    const spouse  = members.find(m => m.relation === 'spouse');
    const children = members.filter(m => m.relation === 'child');
    if (father || mother) {
      familyUnits.push({
        id: 'origin',
        famId: '@F1@',
        partnerA: father ? String(father.id) : '',
        partnerB: mother ? String(mother.id) : '',
        children: ['subject', ...members.filter(m => m.relation === 'sibling').map(m => String(m.id))],
        label: 'Family of origin',
      });
    }
    if (spouse || children.length) {
      familyUnits.push({
        id: 'subject_family',
        famId: familyUnits.length === 0 ? '@F1@' : '@F2@',
        partnerA: 'subject',
        partnerB: spouse ? String(spouse.id) : '',
        children: children.map(m => String(m.id)),
        label: 'Subject family',
      });
    }
  }

  familyUnits.forEach(family => {
    [family.partnerA, family.partnerB].filter(Boolean).forEach(ref => {
      if (!famsLinks[ref]) famsLinks[ref] = [];
      famsLinks[ref].push(family.famId);
    });
    family.children.forEach(ref => {
      if (!famcLinks[ref]) famcLinks[ref] = [];
      famcLinks[ref].push(family.famId);
    });
  });

  // ── SUBJECT INDIVIDUAL ────────────────────────────────────────
  const subjName = splitName(subjectName || '');
  lines.push(`0 ${SUBJ_ID} INDI`);
  lines.push(`1 NAME ${subjName.given} /${subjName.surname || subjName.given}/`);
  lines.push(`2 GIVN ${subjName.given}`);
  if (subjName.surname) lines.push(`2 SURN ${subjName.surname}`);
  if (subjectRecord?.born) {
    lines.push('1 BIRT');
    lines.push('2 DATE ' + gedYear(subjectRecord.born));
    if (subjectRecord.birthplace) lines.push('2 PLAC ' + esc(subjectRecord.birthplace));
  }
  if (subjectRecord?.death_year) {
    lines.push('1 DEAT');
    lines.push('2 DATE ' + gedYear(subjectRecord.death_year));
  }
  if (subjectRecord?.auschwitz_number) {
    lines.push('1 NOTE Auschwitz prisoner number: ' + esc(subjectRecord.auschwitz_number));
  }
  if (subjectRecord?.prewar_address) {
    lines.push('1 RESI');
    lines.push('2 PLAC ' + esc(subjectRecord.prewar_address));
    lines.push('2 NOTE Pre-war address');
  }
  if (subjectRecord?.notes) {
    lines.push('1 NOTE ' + esc(subjectRecord.notes));
  }
  (famcLinks.subject || []).forEach(famId => lines.push(`1 FAMC ${famId}`));
  (famsLinks.subject || []).forEach(famId => lines.push(`1 FAMS ${famId}`));

  // ── FAMILY MEMBERS ────────────────────────────────────────────
  members.forEach(m => {
    const mid  = memberIds[m.id];
    const nm   = splitName(m.name);
    lines.push(`0 ${mid} INDI`);
    lines.push(`1 NAME ${nm.given} /${nm.surname || nm.given}/`);
    lines.push(`2 GIVN ${nm.given}`);
    if (nm.surname) lines.push(`2 SURN ${nm.surname}`);

    // Sex inference from relation
    const sexMap = {
      father: 'M', grandfather: 'M', son_in_law: 'M', brother_in_law: 'M',
      grandson: 'M', uncle: 'M', nephew: 'M',
      mother: 'F', grandmother: 'F', daughter_in_law: 'F', sister_in_law: 'F',
      granddaughter: 'F', aunt: 'F', niece: 'F',
      spouse: 'U',
    };
    if (sexMap[m.relation]) lines.push(`1 SEX ${sexMap[m.relation]}`);

    if (m.birth_year) {
      lines.push('1 BIRT');
      lines.push('2 DATE ' + gedYear(m.birth_year));
      if (m.birth_place) lines.push('2 PLAC ' + esc(m.birth_place));
    }
    if (m.death_year) {
      lines.push('1 DEAT Y');
      lines.push('2 DATE ' + gedYear(m.death_year));
      if (m.fate_detail) lines.push('2 NOTE ' + esc(m.fate_detail));
    } else if (m.fate === 'killed') {
      lines.push('1 DEAT Y');
      if (m.fate_detail) lines.push('2 NOTE ' + esc(m.fate_detail));
    }

    // Fate as a custom note
    if (m.fate && m.fate !== 'unknown') {
      const fateText = { survived: 'Survived the Holocaust', killed: 'Killed — ' + (m.fate_detail || 'details unknown'), emigrated: 'Emigrated', }[m.fate] || m.fate;
      lines.push('1 NOTE ' + esc(fateText));
    }
    if (m.notes) lines.push('1 NOTE ' + esc(m.notes));

    // Group membership notes
    const memberGroups = (groupList || []).filter(g => (g.memberIds || []).includes(String(m.id)));
    memberGroups.forEach(g => lines.push(`1 NOTE Group: ${esc(g.name)}`));

    // Family links
    (famsLinks[String(m.id)] || []).forEach(famId => lines.push(`1 FAMS ${famId}`));
    (famcLinks[String(m.id)] || []).forEach(famId => lines.push(`1 FAMC ${famId}`));
  });

  // ── FAMILY RECORDS ────────────────────────────────────────────
  familyUnits.forEach(family => {
    lines.push(`0 ${family.famId} FAM`);
    if (family.partnerA) lines.push(`1 HUSB ${family.partnerA === 'subject' ? SUBJ_ID : memberIds[family.partnerA]}`);
    if (family.partnerB) lines.push(`1 WIFE ${family.partnerB === 'subject' ? SUBJ_ID : memberIds[family.partnerB]}`);
    if (family.label) lines.push(`1 NOTE ${esc(family.label)}`);
    family.children.forEach(childRef => {
      const childId = childRef === 'subject' ? SUBJ_ID : memberIds[childRef];
      if (childId) lines.push(`1 CHIL ${childId}`);
    });
  });

  // ── TRAILER ───────────────────────────────────────────────────
  lines.push('0 TRLR');

  return lines.join('\r\n'); // GEDCOM spec requires CRLF
}
